import React, { useState, useEffect } from 'react';
import { IconWrapper, Wrapper, NodeWrapper } from '../rotation-tree/RotationTree.styles';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import { Rotation } from '../../types/ViewModel/Rotation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReportLocationEnum } from '../../enums/reportLocationEnum';
import { RotationTreeEnum } from '../../enums/rotationTreeEnum';
import { PortCall } from '../../types/ViewModel/PortCall';
import queryParamsUtils from '../../utils/queryParamsUtils';
import UpdateOrderStatus from './chemical-order/UpdateOrderStatus';
import OrderStatusIconList from './chemical-order/OrderStatusIconList';
import { isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { getRobIcon } from '../ui/status-icon/CargoConsumablesIcon';

type Props = {
    rotationTree: Rotation;
    selectedPortCall: PortCall;
};

const priorityPorts: string[] = ['Rotterdam', 'Houston - Texas City', 'Singapore', 'Ulsan', 'Durban'];

const TankCleaningRotationTreeComponent: React.FC<Props> = (props) => {
    const [openedRotationNodeIndex, setOpenedRotationNodeIndex] = useState<number | null>(-2);
    const [portCallArray, setPortCallArray] = useState<PortCall[]>([]);
    const { setQueryParamChemicalPortCall } = queryParamsUtils();

    // =============  Use Effects ================

    useEffect(() => {
        if (props.rotationTree && props.selectedPortCall) {
            const _tankPortCallArray = props.rotationTree.portCallArray.filter((portCall) => portCall.reportLocation === ReportLocationEnum.Port);
            setPortCallArray(_tankPortCallArray);
        }
    }, [props.rotationTree, props.selectedPortCall]);

    const onSelectedPortNode = (node: IRotationNode | null, portIndex: number) => (event) => {
        event.stopPropagation();
        if (node && node.id && node.id > 0 && portIndex >= 0) {
            if (portIndex !== openedRotationNodeIndex) {
                setOpenedRotationNodeIndex(portIndex);
            } else {
                setOpenedRotationNodeIndex(-1);
            }
            setQueryParamChemicalPortCall(node.id.toString());
        }
    };

    const getPortClusterIcon = (portCallItem: PortCall) => {
        if (portCallItem.isInPortCluster && portCallItem.reportLocation === ReportLocationEnum.Port) {
            return (
                <span className="is-in-port-cluster">
                    <FontAwesomeIcon title="Port cluster" icon="project-diagram" />
                </span>
            );
        }

        return null;
    };

    const getPrioritizePortIcon = (portCallItem: PortCall) => {
        if (priorityPorts.find((p) => p.toLocaleLowerCase() === portCallItem.port?.portName.toLocaleLowerCase())) {
            return (
                <span className="is-priority-port">
                    <FontAwesomeIcon title="Priority port - should be used for order - if possible" icon="star" />
                </span>
            );
        }

        return null;
    };

    const isRotationIdEqualCss = (
        node: IRotationNode | null,
        pcId: number,
        bcId: number | null,
        coId: number | null,
        reportLocation: ReportLocationEnum | null
    ): string => {
        let isEqual = false;
        if (node) {
            switch (node.type) {
                case RotationTreeEnum.Sea:
                case RotationTreeEnum.PortCall: {
                    const pc = node as PortCall;
                    isEqual = pc.id === pcId && !bcId && !coId && pc.reportLocation === reportLocation;
                    break;
                }
            }
        }
        return isEqual ? 'is-selected' : '';
    };

    const getUpdateOrderStatus = (portCallItem: PortCall): number => {
        return portCallItem.chemicalOrderForPortCallList
            .sort((a, b) => (isDateBefore(a.createdUtc, b.createdUtc) ? 1 : -1))
            .filter((c) => c.parentOrderId === 0)[0]?.updateOrderStatusId;
    };

    const createRotation = () => {
        return (
            <Wrapper>
                <div className="rotation-tree-list">
                    {portCallArray &&
                        portCallArray.length > 0 &&
                        portCallArray.map((portCallItem, portIndex) => {
                            return (
                                <NodeWrapper
                                    className={`rotation-parent-node`}
                                    key={`p-` + portCallItem.portCallId + `-` + portCallItem.reportLocation}
                                >
                                    <div
                                        className={`rotation-node port-node 
                                            ${isRotationIdEqualCss(props.selectedPortCall, portCallItem.id, null, null, portCallItem.reportLocation)}
                                        `}
                                        onClick={onSelectedPortNode(portCallItem, portIndex)}
                                    >
                                        <span className="rotation-name">
                                            <span>{portCallItem.voyageNumberPresentationString}</span>
                                            <span className="sep"> - </span>
                                            {portCallItem.name}
                                            {getPortClusterIcon(portCallItem)}

                                            <IconWrapper>
                                                {portCallItem.chemicalOrderForPortCallList.length > 0 &&
                                                    portCallItem.chemicalOrderForPortCallList[0]?.lastOrderStatusId && (
                                                        <>
                                                            <OrderStatusIconList
                                                                chemicalOrderForPortCallList={portCallItem.chemicalOrderForPortCallList}
                                                                iconsRightPos={40}
                                                            />
                                                            <UpdateOrderStatus
                                                                updateOrderStatus={getUpdateOrderStatus(portCallItem)}
                                                                showIcon={true}
                                                                inList={true}
                                                            />
                                                        </>
                                                    )}
                                                {getPrioritizePortIcon(portCallItem)}
                                                {getRobIcon(portCallItem)}
                                            </IconWrapper>
                                        </span>
                                    </div>
                                </NodeWrapper>
                            );
                        })}
                </div>
            </Wrapper>
        );
    };

    return <>{createRotation()}</>;
};

export default TankCleaningRotationTreeComponent;
